import React, { FormEvent } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { ServicesContext, useAdminService } from '~/services';
import { CreateMaclModal } from '~/components/Admin';
import { PaginationType, emptyPagination, useServiceSubscribe } from '~/util';
import { Pagination } from '~/components/Pagination';
import { MaclRow } from './MaclRow';
import { MaclType } from '../Edit/MaclForm';

export function ListMacls() {
    const servicesContext = React.useContext(ServicesContext);
    const { getMacls, events } = useAdminService();
    const [loading, setLoading] = React.useState(false);
    const [list, setList] = React.useState<MaclType[]>([]);
    const nameRef = React.useRef('');
    const paginationRef = React.useRef(emptyPagination());

    function getParams() {
        const params: {
            q: string;
            page: null | number;
            items_per_page: null | number;
            name?: string;
        } = {
            q: '',
            page: paginationRef.current.current,
            items_per_page: paginationRef.current.items_per_page,
        };

        // Search
        if (nameRef.current) {
            params.name = nameRef.current;
        }

        return params;
    }

    const loadMacls = React.useCallback(async () => {
        const params = getParams();

        window.scrollTo(0, 0);
        setLoading(true);
        try {
            const { results, pagination } = await getMacls(params, {
                handledErrors: { 400: true },
            });
            paginationRef.current = pagination;
            setList(results);
        } catch (e) {
            servicesContext.alert.set(e.message, 'danger');
        } finally {
            setLoading(false);
        }
    }, [servicesContext.alert, getMacls]);

    useServiceSubscribe(events.macl_list_updated, loadMacls);

    function handleSearchSubmit(event: FormEvent) {
        event.preventDefault();
        paginationRef.current = {
            ...emptyPagination(),
            items_per_page: paginationRef.current.items_per_page,
        };
        loadMacls();
    }

    function updatePagination(pagination: PaginationType) {
        paginationRef.current = { ...pagination };
        loadMacls();
    }

    React.useEffect(() => {
        loadMacls();
    }, [loadMacls]);

    return servicesContext.user.isMACLAdmin() ? (
        <div>
            <div className="d-flex justify-content-between mb-2">
                <div>
                    <form onSubmit={handleSearchSubmit}>
                        <div className="input-group search-by-split-input">
                            <div className="input-group-prepend">
                                <span className="input-group-text">MACL Name:</span>
                            </div>
                            <input
                                type="text"
                                id="macl_search"
                                aria-label="macl_search"
                                className="form-control"
                                onChange={(event) => {
                                    nameRef.current = event.target.value;
                                }}
                            />
                            <div className="input-group-append">
                                <button type="submit" className="btn btn-success">
                                    Search
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
                <div className="ms-2">
                    <CreateMaclModal />
                </div>
            </div>
            <div className="table-scroll">
                <Table striped bordered hover>
                    <thead className="table-dark">
                        <tr>
                            <th>Name</th>
                            <th>Members</th>
                            <th>
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={3} className="text-center bg-white">
                                    <Spinner animation="border">
                                        <span className="sr-only">Loading MACLs</span>
                                    </Spinner>
                                </td>
                            </tr>
                        )}
                        {list.map((macl) => (
                            <MaclRow key={macl.id} macl={macl} />
                        ))}
                    </tbody>
                </Table>
            </div>
            <Pagination pagination={paginationRef.current} updatePagination={updatePagination} />
        </div>
    ) : null;
}
